const itemSelector = "[role='menuitem']";
const controllers = new Set();

export class SiteNavigationController {
  constructor(root) {
    this.root = root;
    this.document = root.ownerDocument;
    this.drawerTrigger = root.querySelector("[data-sitenav-drawer-trigger]");
    this.drawer = root.querySelector("[data-sitenav-drawer]");

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onFocusout = this.onFocusout.bind(this);

    root.addEventListener("click", this.onClick);
    root.addEventListener("keydown", this.onKeydown);
    root.addEventListener("focusout", this.onFocusout);
    this.document.addEventListener("click", this.onDocumentClick);
  }

  disconnect() {
    this.root.removeEventListener("click", this.onClick);
    this.root.removeEventListener("keydown", this.onKeydown);
    this.root.removeEventListener("focusout", this.onFocusout);
    this.document.removeEventListener("click", this.onDocumentClick);
  }

  onClick(event) {
    const trigger = event.target.closest("[data-sitenav-trigger]");
    if (trigger && this.root.contains(trigger)) {
      const open = trigger.getAttribute("aria-expanded") === "true";
      open ? this.closeMenu(trigger, { restoreFocus: true }) : this.openMenu(trigger);
      return;
    }

    if (event.target.closest("[data-sitenav-drawer-trigger]")) {
      const open = this.drawerTrigger.getAttribute("aria-expanded") === "true";
      open ? this.closeDrawer({ restoreFocus: true }) : this.openDrawer();
      return;
    }

    if (event.target.closest("[data-sitenav-menu] a, [data-sitenav-drawer] a")) {
      this.closeAll({ restoreFocus: false });
    }
  }

  // An outside click closes without moving focus: the reader has just put it
  // somewhere, and pulling it back to the trigger would take it out of the
  // field they clicked into.
  onDocumentClick(event) {
    if (!this.root.contains(event.target)) this.closeAll({ restoreFocus: false });
  }

  // Tab out of an open menu or the drawer closes it. The items are tabindex -1,
  // so Tab from inside a menu lands on the next bucket's trigger — which is
  // outside the menu and must not leave it hanging open underneath.
  onFocusout(event) {
    const next = event.relatedTarget;
    if (!next) return;

    const trigger = this.root.querySelector("[data-sitenav-trigger][aria-expanded='true']");
    if (trigger && next !== trigger && !this.controlledElement(trigger)?.contains(next)) {
      this.closeMenu(trigger);
    }

    const drawerOpen = this.drawerTrigger?.getAttribute("aria-expanded") === "true";
    if (drawerOpen && next !== this.drawerTrigger && !this.drawer.contains(next)) {
      this.closeDrawer();
    }
  }

  onKeydown(event) {
    const trigger = event.target.closest("[data-sitenav-trigger]");
    if (trigger && ["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      this.openMenu(trigger, { focus: event.key === "ArrowUp" ? "last" : "first" });
      return;
    }

    if (event.target === this.drawerTrigger && ["Enter", " ", "ArrowDown"].includes(event.key)) {
      event.preventDefault();
      this.openDrawer({ focusFirst: true });
      return;
    }

    const menu = event.target.closest("[data-sitenav-menu]");
    if (menu) {
      this.moveMenuFocus(event, menu);
      return;
    }

    if (this.drawer?.contains(event.target)) this.moveDrawerFocus(event);

    if (event.key === "Escape") this.closeAll({ restoreFocus: true });
  }

  openMenu(trigger, { focus = null } = {}) {
    this.closeAll({ restoreFocus: false });
    const menu = this.controlledElement(trigger);
    if (!menu) return;

    trigger.setAttribute("aria-expanded", "true");
    menu.hidden = false;

    const items = [...menu.querySelectorAll(itemSelector)];
    if (focus === "first") items[0]?.focus();
    if (focus === "last") items.at(-1)?.focus();
  }

  closeMenu(trigger, { restoreFocus = false } = {}) {
    const menu = this.controlledElement(trigger);
    trigger.setAttribute("aria-expanded", "false");
    if (menu) menu.hidden = true;
    if (restoreFocus) trigger.focus();
  }

  openDrawer({ focusFirst = false } = {}) {
    this.closeAll({ restoreFocus: false });
    if (!this.drawer || !this.drawerTrigger) return;

    this.drawerTrigger.setAttribute("aria-expanded", "true");
    this.drawer.hidden = false;
    if (focusFirst) this.drawer.querySelector("a")?.focus();
  }

  closeDrawer({ restoreFocus = false } = {}) {
    if (!this.drawer || !this.drawerTrigger) return;

    this.drawerTrigger.setAttribute("aria-expanded", "false");
    this.drawer.hidden = true;
    if (restoreFocus) this.drawerTrigger.focus();
  }

  closeAll({ restoreFocus = false } = {}) {
    const openTrigger = this.root.querySelector("[data-sitenav-trigger][aria-expanded='true']");
    const drawerWasOpen = this.drawerTrigger?.getAttribute("aria-expanded") === "true";

    this.root.querySelectorAll("[data-sitenav-trigger]").forEach((trigger) => {
      this.closeMenu(trigger);
    });
    this.closeDrawer();

    if (restoreFocus) {
      if (openTrigger) openTrigger.focus();
      else if (drawerWasOpen) this.drawerTrigger?.focus();
    }
  }

  moveMenuFocus(event, menu) {
    const items = [...menu.querySelectorAll(itemSelector)];
    const current = items.indexOf(event.target);
    let next;

    if (["ArrowDown", "ArrowRight"].includes(event.key)) next = (current + 1) % items.length;
    if (["ArrowUp", "ArrowLeft"].includes(event.key)) next = (current - 1 + items.length) % items.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = items.length - 1;

    if (next !== undefined) {
      event.preventDefault();
      items[next]?.focus();
    } else if (event.key === "Escape") {
      event.preventDefault();
      const trigger = this.root.querySelector(`[aria-controls='${menu.id}']`);
      if (trigger) this.closeMenu(trigger, { restoreFocus: true });
    }
  }

  moveDrawerFocus(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.closeDrawer({ restoreFocus: true });
      return;
    }

    const links = [...this.drawer.querySelectorAll("a")];
    const current = links.indexOf(event.target);
    let next;
    if (event.key === "ArrowDown") next = (current + 1) % links.length;
    if (event.key === "ArrowUp") next = (current - 1 + links.length) % links.length;
    if (next !== undefined) {
      event.preventDefault();
      links[next]?.focus();
    }
  }

  controlledElement(trigger) {
    const id = trigger.getAttribute("aria-controls");
    const controlled = id ? this.document.getElementById(id) : null;
    return controlled && this.root.contains(controlled) ? controlled : null;
  }
}

export function start() {
  document.querySelectorAll("[data-site-navigation]:not([data-site-navigation-ready])")
    .forEach((root) => {
      root.dataset.siteNavigationReady = "true";
      controllers.add(new SiteNavigationController(root));
    });
}

function stop() {
  controllers.forEach((controller) => controller.disconnect());
  controllers.clear();
}

document.addEventListener("DOMContentLoaded", start);
document.addEventListener("turbo:load", start);
document.addEventListener("turbo:before-render", stop);
